import { GetServerSideProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from '../_app';
import Head from 'next/head';
import Layout from '@/layouts';
import { AppSettings } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import { useMemo } from 'react';
import { canonicalUrl } from '@/shared/utils';
import { getTdkApi } from '@/lib/service/tdk';
import { page2JsonLd } from '@/lib/jsonld';

// 动态导入主组件
const UserTestimonials = dynamic(() => import('@/components/UserTestimonials'), {
  ssr: true,
});

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { locale, req, resolvedUrl } = context;

  const [localeRes, tdkRes] = await Promise.all([
    serverSideTranslations(locale, ['common']),
    getTdkApi(resolvedUrl.split('?')[0], locale),
  ]);

  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};
  const siteUrl = locale !== 'en' ? `https://${req.headers.host}/${locale}` : `https://${req.headers.host}`;

  const jsonLd = page2JsonLd(
    {
      title: tdkRes?.data?.title ?? 'User Testimonials | Dolphin Radar',
      desc: tdkRes?.data?.description ?? 'See what real Instagram users are saying about Dolphin Radar',
      url: canonicalUrl(req.headers.host, resolvedUrl, locale),
    },
    {
      url: siteUrl,
    }
  );

  return {
    props: {
      ...localeRes,
      herf: canonicalUrl(req.headers.host, resolvedUrl, locale),
      title: tdkRes?.data?.title ?? tempData['head_UserTestimonials_Meta_Title'] ?? 'User Testimonials | Dolphin Radar',
      description:
        tdkRes?.data?.description ??
        tempData['head_UserTestimonials_Meta_Desc'] ??
        'See what real Instagram users are saying about Dolphin Radar',
      jsonLd,
    },
  };
};

const IndexPage: NextPageWithLayout<InferGetStaticPropsType<typeof getServerSideProps>> = () => {
  const getContent = useMemo(() => <UserTestimonials />, []);
  return <>{getContent}</>;
};

IndexPage.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const description = page?.props?.description;

  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={description} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={description} />
          <meta property="og:image" content={AppSettings.logoUrl} />
          <meta property="og:image:secure_url" content={AppSettings.logoUrl} />
          <meta property="og:type" content="summary" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={description} />
          <meta name="twitter:image" content={AppSettings.logoUrl} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <link rel="icon" href={AppSettings.icoPath} />
          <link rel="canonical" href={page.props?.herf} />
          {page.props?.jsonLd && (
            <script
              async
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(page.props?.jsonLd) }}
            />
          )}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default IndexPage;
